import { Banknote, RotateCcw, TrendingUp, X } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { CashDeploymentMode } from "../../../utils/cashDeploymentSettings";
import { formatCurrencyWithCommas, formatWeight } from "../../../utils/numberUtils";
import {
  EXPLAINER_DEPOSIT,
  EXPLAINER_FINAL,
  EXPLAINER_PHASE_LABELS,
  EXPLAINER_PHASE_ORDER,
  EXPLAINER_PRODUCT_LABEL,
  EXPLAINER_TARGET_CASH_PCT,
  EXPLAINER_TARGET_PRODUCT_PCT,
  type ExplainerPhase,
  formatExplainerSplitLabel,
  getExplainerSnapshot,
} from "./cashDeploymentExplainerDemo";

interface CashDeploymentExplainerModalProps {
  isOpen: boolean;
  onClose: () => void;
  appearance?: "default" | "alpha";
}

const CashDeploymentExplainerModal: React.FC<CashDeploymentExplainerModalProps> = ({
  isOpen,
  onClose,
  appearance = "default",
}) => {
  const isAlpha = appearance === "alpha";
  const [mode, setMode] = useState<CashDeploymentMode>("auto-deploy");
  const [phase, setPhase] = useState<ExplainerPhase>("start");

  const snapshot = useMemo(() => getExplainerSnapshot(mode, phase), [mode, phase]);
  const productWidth = (snapshot.productDollars / snapshot.nlv) * 100;
  const cashWidth = (snapshot.cashDollars / snapshot.nlv) * 100;
  const showDepositBadge = phase !== "start";
  const showDepositPulse = phase === "deposit" || phase === "settle";
  const splitHeld = mode === "auto-deploy" && phase === "complete";
  const activeStepIndex = EXPLAINER_PHASE_ORDER.indexOf(phase);

  const resetToStart = useCallback(() => {
    setPhase("start");
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    resetToStart();
  }, [isOpen, resetToStart]);

  useEffect(() => {
    resetToStart();
  }, [mode, resetToStart]);

  if (!isOpen) return null;

  const microLabelClass = isAlpha
    ? "text-[10px] font-semibold uppercase tracking-wider text-v7-ink/55"
    : "text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400";
  const cardClass = isAlpha
    ? "rounded-v7-panel border border-v7-ink/15 bg-v7-white"
    : "rounded-lg border border-gray-200 bg-white dark:border-rp-separator dark:bg-black";
  const strongTextClass = isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white";
  const bodyTextClass = isAlpha ? "text-v7-ink-soft" : "text-gray-600 dark:text-gray-300";

  return (
    <div className={`fixed inset-0 z-[60] flex items-center justify-center p-4 ${isAlpha ? "bg-v7-ink/60" : "bg-black/70"}`}>
      <div
        data-appearance={appearance}
        className={`flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden border shadow-2xl ${
          isAlpha
            ? "rounded-v7-popover border-v7-ink/15 bg-v7-white font-v7-sans"
            : "rounded-xl border-gray-200 bg-white dark:border-rp-separator dark:bg-rp-surface-raised"
        }`}
      >
        <div className={`flex items-start justify-between border-b px-5 py-4 ${isAlpha ? "border-v7-ink/10" : "border-gray-200 dark:border-rp-separator"}`}>
          <div>
            <h2 className={`text-base font-bold leading-tight ${strongTextClass}`}>
              How new cash deployment works
            </h2>
            <p className={`mt-1 text-sm ${bodyTextClass}`}>
              Simple example: a $100,000 account split half invested and half cash, then a $20,000 deposit.
            </p>
            <p className={`mt-2 text-xs ${isAlpha ? "text-v7-ink/60" : "text-gray-500 dark:text-gray-400"}`}>
              Click each step below to walk through the example at your own pace.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={isAlpha
              ? "rounded-v7-control p-1 text-v7-ink/45 transition-colors hover:bg-v7-paper hover:text-v7-ink"
              : "rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-rp-surface dark:hover:text-white"}
            aria-label="Close explainer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 overflow-y-auto p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className={`inline-flex border p-0.5 ${isAlpha ? "rounded-v7-control border-v7-ink/20 bg-v7-white" : "rounded-lg border-gray-200 bg-white dark:border-rp-separator dark:bg-rp-surface"}`}>
              {(["auto-deploy", "reserve"] as CashDeploymentMode[]).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setMode(option)}
                  className={
                    mode === option
                      ? isAlpha
                        ? "rounded-v7-control bg-v7-bronze/10 px-3 py-1.5 text-xs font-semibold text-v7-bronze-dark transition-colors"
                        : "rounded-md bg-rp-amber px-3 py-1.5 text-xs font-semibold text-white transition-colors"
                      : isAlpha
                        ? "rounded-v7-control px-3 py-1.5 text-xs font-semibold text-v7-ink-soft transition-colors hover:text-v7-ink"
                        : "rounded-md px-3 py-1.5 text-xs font-semibold text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  }
                >
                  {option === "auto-deploy" ? "Auto-deploy" : "Reserve cash"}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={resetToStart}
              className={isAlpha
                ? "inline-flex items-center gap-2 rounded-v7-control border border-v7-ink/25 bg-v7-white px-3 py-1.5 text-xs font-semibold text-v7-ink-soft transition-colors hover:border-v7-ink"
                : "inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:border-gray-300 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Back to step 1
            </button>
          </div>

          <div className={`relative overflow-hidden border p-5 ${isAlpha ? "rounded-v7-panel border-v7-ink/15 bg-v7-paper" : "rounded-2xl border-gray-200 bg-gray-50 dark:border-rp-separator dark:bg-rp-surface-sunken"}`}>
            <div className={`pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full blur-2xl ${isAlpha ? "bg-v7-bronze/10" : "bg-rp-amber/10"}`} />
            <div className={`pointer-events-none absolute -bottom-10 -left-6 h-28 w-28 rounded-full blur-2xl ${isAlpha ? "bg-v7-ink/10" : "bg-blue-500/10"}`} />

            <div className="relative space-y-5">
              <div className={`px-4 py-3 ${cardClass}`}>
                <p className={microLabelClass}>
                  Your target split
                </p>
                <p className={`mt-1 text-sm font-semibold ${strongTextClass}`}>
                  Half invested, half cash ({formatExplainerSplitLabel(EXPLAINER_TARGET_PRODUCT_PCT, EXPLAINER_TARGET_CASH_PCT)})
                </p>
                <p className={`mt-1 text-xs ${isAlpha ? "text-v7-ink/60" : "text-gray-500 dark:text-gray-400"}`}>
                  On $100,000 that means {formatCurrencyWithCommas(50_000, 0)} invested and {formatCurrencyWithCommas(50_000, 0)} cash.
                </p>
              </div>

              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <p className={microLabelClass}>
                    Total account value
                  </p>
                  <p className={`mt-1 font-mono text-3xl font-bold tabular-nums transition-all duration-700 ${strongTextClass}`}>
                    {formatCurrencyWithCommas(snapshot.nlv, 0)}
                  </p>
                </div>

                <div
                  className={
                    showDepositBadge
                      ? `inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold ${
                        isAlpha
                          ? "border-v7-success/30 bg-v7-success/10 text-v7-success"
                          : "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400"
                      }${showDepositPulse ? " cash-explainer-deposit" : ""}`
                      : `inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold opacity-0 transition-opacity duration-500 ${
                        isAlpha
                          ? "border-v7-ink/15 bg-v7-white text-v7-ink/60"
                          : "border-gray-200 bg-white text-gray-500 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-400"
                      }`
                  }
                >
                  <Banknote className="h-4 w-4" />
                  +{formatCurrencyWithCommas(EXPLAINER_DEPOSIT, 0)} deposit
                </div>
              </div>

              <div>
                <div className={`mb-2 flex items-center justify-between ${microLabelClass}`}>
                  <span>How your money is split</span>
                  <span>{EXPLAINER_PHASE_LABELS[phase]}</span>
                </div>

                <div className={`relative h-14 overflow-hidden border ${isAlpha ? "rounded-v7-panel border-v7-ink/15 bg-v7-white" : "rounded-xl border-gray-200 bg-white dark:border-rp-separator dark:bg-black"}`}>
                  <div className="absolute inset-0 flex">
                    <div
                      className={`relative h-full transition-all duration-700 ease-out ${isAlpha ? "bg-v7-bronze" : "bg-rp-amber"}`}
                      style={{ width: `${productWidth}%` }}
                    >
                      <span className={`absolute inset-y-0 left-3 flex items-center text-xs font-semibold ${isAlpha ? "text-v7-white" : "text-black"}`}>
                        Invested
                      </span>
                    </div>
                    <div
                      className={`relative h-full transition-all duration-700 ease-out ${isAlpha ? "bg-v7-ink" : "bg-blue-500 dark:bg-blue-600"}`}
                      style={{ width: `${cashWidth}%` }}
                    >
                      <span className={`absolute inset-y-0 right-3 flex items-center text-xs font-semibold ${isAlpha ? "text-v7-white" : "text-white"}`}>
                        Cash
                      </span>
                    </div>
                  </div>

                  {phase === "settle" && (
                    <div className={`cash-explainer-shimmer pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent to-transparent ${isAlpha ? "via-white/30" : "via-white/30 dark:via-white/10"}`} />
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className={`p-4 ${cardClass}`}>
                  <div className="mb-2 flex items-center gap-2">
                    <TrendingUp className={`h-4 w-4 ${isAlpha ? "text-v7-bronze-dark" : "text-rp-amber"}`} />
                    <span className={`text-sm font-semibold ${strongTextClass}`}>{EXPLAINER_PRODUCT_LABEL}</span>
                  </div>
                  <p className={`font-mono text-xl font-bold tabular-nums transition-all duration-700 ${strongTextClass}`}>
                    {formatCurrencyWithCommas(snapshot.productDollars, 0)}
                  </p>
                  <p className={`mt-1 font-mono text-sm tabular-nums transition-all duration-700 ${isAlpha ? "text-v7-success" : "text-teal-600 dark:text-teal-400"}`}>
                    {formatWeight(snapshot.productPct, snapshot.productPct % 1 === 0 ? 0 : 2)}% of account
                  </p>
                </div>

                <div className={`p-4 ${cardClass}`}>
                  <div className="mb-2 flex items-center gap-2">
                    <Banknote className={`h-4 w-4 ${isAlpha ? "text-v7-ink" : "text-blue-500"}`} />
                    <span className={`text-sm font-semibold ${strongTextClass}`}>Cash</span>
                  </div>
                  <p className={`font-mono text-xl font-bold tabular-nums transition-all duration-700 ${strongTextClass}`}>
                    {formatCurrencyWithCommas(snapshot.cashDollars, 0)}
                  </p>
                  <p className={`mt-1 font-mono text-sm tabular-nums transition-all duration-700 ${isAlpha ? "text-v7-ink-soft" : "text-blue-600 dark:text-blue-400"}`}>
                    {formatWeight(snapshot.cashPct, snapshot.cashPct % 1 === 0 ? 0 : 2)}% of account
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {EXPLAINER_PHASE_ORDER.map((step, index) => (
                  <button
                    key={step}
                    type="button"
                    onClick={() => setPhase(step)}
                    aria-current={index === activeStepIndex ? "step" : undefined}
                    className={
                      index === activeStepIndex
                        ? isAlpha
                          ? "rounded-v7-control border border-v7-bronze-dark bg-v7-bronze/10 px-2 py-2 text-center transition-colors hover:bg-v7-bronze/15"
                          : "rounded-lg border border-rp-amber bg-rp-amber/10 px-2 py-2 text-center transition-colors hover:bg-rp-amber/15"
                        : isAlpha
                          ? "rounded-v7-control border border-v7-ink/15 bg-v7-white px-2 py-2 text-center transition-colors hover:border-v7-bronze/40 hover:bg-v7-paper"
                          : "rounded-lg border border-gray-200 bg-white px-2 py-2 text-center transition-colors hover:border-rp-amber/40 hover:bg-gray-50 dark:border-rp-separator dark:bg-black dark:hover:bg-rp-surface"
                    }
                  >
                    <p className={microLabelClass}>
                      Step {index + 1}
                    </p>
                    <p className={`mt-1 text-xs font-semibold ${strongTextClass}`}>
                      {EXPLAINER_PHASE_LABELS[step]}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className={`p-4 ${cardClass}`}>
            <p className={`text-sm ${bodyTextClass}`}>
              {mode === "auto-deploy" ? (
                <>
                  With <span className={`font-semibold ${strongTextClass}`}>auto-deploy</span>, the new {formatCurrencyWithCommas(EXPLAINER_DEPOSIT, 0)} is added using the same half-and-half split. Both sides grow equally, so you end up with {formatCurrencyWithCommas(60_000, 0)} invested and {formatCurrencyWithCommas(60_000, 0)} cash.
                </>
              ) : (
                <>
                  With <span className={`font-semibold ${strongTextClass}`}>reserve cash</span>, the deposit stays in cash. Invested dollars stay at {formatCurrencyWithCommas(50_000, 0)}, cash rises to {formatCurrencyWithCommas(70_000, 0)}, and the split updates to reflect the larger account.
                </>
              )}
            </p>
            {splitHeld && (
              <p className={`mt-2 text-xs font-semibold uppercase tracking-wider ${isAlpha ? "text-v7-success" : "text-green-600 dark:text-green-500"}`}>
                Split unchanged: {formatExplainerSplitLabel(EXPLAINER_TARGET_PRODUCT_PCT, EXPLAINER_TARGET_CASH_PCT)}
              </p>
            )}
            {mode === "reserve" && phase === "complete" && (
              <p className={`mt-2 text-xs font-semibold uppercase tracking-wider ${isAlpha ? "text-v7-ink-soft" : "text-blue-600 dark:text-blue-400"}`}>
                Split updated: {formatExplainerSplitLabel(EXPLAINER_FINAL.reserve.productPct, EXPLAINER_FINAL.reserve.cashPct)}
              </p>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes cashExplainerDeposit {
          0%, 100% { transform: translateY(0) scale(1); opacity: 1; }
          50% { transform: translateY(-4px) scale(1.03); opacity: 1; }
        }

        @keyframes cashExplainerShimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        .cash-explainer-deposit {
          animation: cashExplainerDeposit 1.2s ease-in-out infinite;
        }

        .cash-explainer-shimmer {
          animation: cashExplainerShimmer 1.1s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default CashDeploymentExplainerModal;
